/* eslint you-dont-need-lodash-underscore/flatten: 0 */

import { CSSInterpolation } from '@emotion/css/create-instance'
import { css } from '@emotion/react'
import flatten from 'lodash/flatten'
import React from 'react'

import { joinOptionalStringsToSpaceSeparatedString } from '../../util/joinOptionalStringsToSpaceSeparatedString'
import { CssVars, isLinearGradientColor, NeutralColorToken } from '../themes'
import { Card } from './Card'
import { Chip } from './Chip'
import { FoundationIcon } from './FoundationIcon'
import type { Icon16 } from './FoundationIcon/icons'
import { getInlineButtonStyles, TextButton } from './TextButton'
import { getColorValue, useTheme } from './ThemeProvider'
import { Type } from './Type'
import { useUniqueId } from './useUniqueId'

const MAX_NUM_FULLY_VISIBLE_OPTIONS = 5

const styles = {
  menuOption: css({
    boxSizing: 'border-box',
    cursor: 'default',
    display: 'flex',
    minHeight: CssVars.size.cellHeightSm,
    paddingBottom: CssVars.size.gutterWidthSm,
    paddingLeft: CssVars.size.gutterWidth,
    paddingRight: CssVars.size.gutterWidth,
    paddingTop: CssVars.size.gutterWidthSm,
  }),
  menuOptionColumn: css({
    alignContent: 'flex-start',
    alignItems: 'center',
    display: 'flex',
    flexDirection: 'row',
    width: '100%',
  }),
  menuOptionColumnCaption: css({ flexGrow: 1, flexShrink: 1 }),
  menuOptionColumnTicker: css({
    flexBasis: `calc(8 * ${CssVars.size.gutterWidthSm})`,
    flexGrow: 0,
    flexShrink: 0,
    paddingRight: CssVars.size.gutterWidth,
  }),
  menuOptionHighlighted: css({
    backgroundColor: CssVars.neutral.bg2,
  }),
  menuOptionIcon: css({
    display: 'inline-flex',
    marginRight: CssVars.size.gutterWidthSm,
    marginTop: 2,
  }),
  menuOptionInset: css({
    paddingLeft: `calc(3 * ${CssVars.size.gutterWidth})`,
  }),
  menuOptionTexts: css({
    boxSizing: 'border-box',
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    width: '100%',
  }),
  menuOptionTitle: css({
    borderBottom: `1px solid ${CssVars.neutral.bg3}`,
    marginBottom: CssVars.size.gutterWidthXs,
    paddingBottom: `calc(${CssVars.size.gutterWidth} - 1px)`, // subtract one for the border
  }),
  menuOptionTitleContent: css({
    alignContent: 'space-between',
    display: 'flex',
    flexDirection: 'row',
  }),
  menuOptionTitleSpacer: css({
    flexGrow: 1,
    flexShrink: 1,
  }),
  menuOptionsGroup: css({
    border: 0,
    listStyle: 'none',
    margin: 0,
    padding: 0,
  }),
  menuOptionsList: css({
    paddingBottom: CssVars.size.gutterWidthXs,
    paddingTop: CssVars.size.gutterWidthXs,
  }),
}

const stylesVariantSearch = {
  menuOption: css({
    boxSizing: 'border-box',
    cursor: 'default',
    display: 'flex',
    flexBasis: `calc(100% + 6 * ${CssVars.size.gutterWidth})`,
    flexGrow: 0,
    flexShrink: 0,
    margin: `0 calc(-3 * ${CssVars.size.gutterWidth})`,
    minHeight: CssVars.size.cellHeightSm,
    padding: `
      calc(${CssVars.size.gutterWidthSm} / 2)
      calc((3 * ${CssVars.size.gutterWidth}) + (${CssVars.size.gutterWidthSm} / 2))
    `,
    width: `calc(100% + 6 * ${CssVars.size.gutterWidth})`,
  }),
  menuOptionChip: css({
    padding: `calc(${CssVars.size.gutterWidthSm} / 2)`,
  }),
  menuOptionTitle: css({
    alignContent: 'space-inbetween',
    alignItems: 'center',
    display: 'flex',
    flexDirection: 'row',
    minHeight: 'unset', // unset the minHeight from styles.menuOption
    paddingBottom: `calc(${CssVars.size.gutterWidthSm} / 2)`,
    paddingTop: `calc(${CssVars.size.gutterWidthSm} / 2)`,
  }),
  menuOptionsGroup: css({
    border: 0,
    boxSizing: 'border-box',
    display: 'flex',
    flexDirection: 'row',
    flexWrap: 'wrap',
    listStyle: 'none',
    margin: `calc(${CssVars.size.gutterWidthSm} / -2)`,
    padding: `
      ${CssVars.size.gutterWidth}
      calc(3 * ${CssVars.size.gutterWidth})
    `,
    width: `calc(100% + ${CssVars.size.gutterWidthSm})`,
  }),
  menuOptionsList: css({
    paddingBottom: CssVars.size.gutterWidth,
    paddingTop: CssVars.size.gutterWidth,
  }),
  menuOptionsListError: css({
    paddingBottom: CssVars.size.gutterWidth,
    paddingLeft: `calc(3 * ${CssVars.size.gutterWidth})`,
    paddingRight: `calc(3 * ${CssVars.size.gutterWidth})`,
    paddingTop: CssVars.size.gutterWidth,
  }),
}

type MenuOptionBase = {
  /**
   * Label shown for this menu option.
   */
  label: string

  /**
   * Optional testid attribute attached to the outermost `<li>` element.
   */
  'data-testid'?: string
}

export type MenuOptionVariantSelectable<TValue extends string = string> =
  | MenuOptionVariantDefault<TValue>
  | MenuOptionVariantColumn<TValue>
  | MenuOptionVariantChip<TValue>

type MenuOptionVariantSelectableBase<TValue extends string = string> =
  MenuOptionBase & {
    /**
     * Unique value that identifies this menu option.
     */
    value: TValue

    /**
     * Optional text shown in the control when this option is selected.
     * Defaults to the option's label.
     */
    controlLabel?: string

    /**
     * String value to indicate if the option is disabled
     * Disabled options are still tabbable, but can't be selected.
     * This only takes effect on the "default" or missing variant.
     */
    state?: 'default' | 'disabled'
  }

// Do not export from Bento Web; this is only used for test code.
export type MenuOptionVariantDefault<TValue extends string = string> =
  MenuOptionVariantSelectableBase<TValue> & {
    variant?: 'default'

    /**
     * Optional extra description for the label when the dropdown is expanded
     */
    caption?: string

    /**
     * Optional icon, shown prefixed to left of the label and caption texts
     */
    icon?: Icon16
  }

type MenuOptionVariantColumn<TValue extends string = string> =
  MenuOptionVariantSelectableBase<TValue> & {
    variant: 'column'

    /**
     * Optional extra description to the right of the label
     */
    caption?: string
  }

type MenuOptionVariantChip<TValue extends string = string> =
  MenuOptionVariantSelectableBase<TValue> & {
    variant: 'chip'

    /**
     * Optional attachment for chip.
     */
    accessory?: React.ComponentProps<typeof Chip>['accessory']
  }

type MenuOptionVariantButton = MenuOptionBase & {
  /**
   * `button` variant option, which is distinct from the default option by:
   * - when selected will not fire the picker component's `onChange`
   * - instead, which will trigger the associated `onClick` below
   * - icon is required
   */
  variant: 'button'

  /**
   * An icon shown prefixed to left of the label and caption texts
   */
  icon: Icon16

  /**
   * Associated callback called when this option is selected.
   */
  onClick: () => void
}

type MenuOptionVariantTitle = MenuOptionBase & {
  variant: 'title'
  // HACK(yan): DO NOT USE. show more link is a legacy design, will be removed
  //  in the near future.
  __DO_NOT_USE__DeprecatedShowMoreLink?: string
}

export type MenuOptionType<
  TOption extends MenuOptionVariantSelectable = MenuOptionVariantSelectable
> = TOption | MenuOptionVariantButton | MenuOptionVariantTitle

export function __DO_NOT_USE_JUST_FOR_DOCS_MenuOptionVariantSelectable<
  TValue extends string
>(
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  props: MenuOptionVariantSelectable<TValue>
) {
  return null
}

export function __DO_NOT_USE_JUST_FOR_DOCS_MenuOptionVariantButton(
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  props: MenuOptionVariantButton
) {
  return null
}

export function __DO_NOT_USE_JUST_FOR_DOCS_MenuOptionVariantTitle(
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  props: MenuOptionVariantTitle
) {
  return null
}

type Props<TOption extends MenuOptionVariantSelectable> = {
  index?: number
  innerRef: React.Ref<HTMLLIElement>
  isHighlighted?: boolean
  isSelected?: boolean
  isInset?: boolean
  maxVisibleOptions?: number
  option: MenuOptionType<TOption>
  substring?: string
  variant?: 'default' | 'search'
} & React.HTMLAttributes<HTMLLIElement>

/**
 * `MenuOption` is an internal component that powers the dropdown menu of
 * `SelectMenu`, `OverflowMenu`, and `Autocomplete`.
 *
 * This component is not intended to be used directly. Instead, controlled
 * through the shape of the `MenuOption` typed object passed to the
 * aforementioned components via their `options` prop.
 */
export function MenuOption<TOption extends MenuOptionVariantSelectable>({
  index,
  innerRef,
  isHighlighted = false,
  isInset = false,
  isSelected = false,
  maxVisibleOptions,
  option,
  role,
  substring,
  variant,
  ...props
}: Props<TOption>) {
  let content = null
  if (option.variant === 'chip') {
    content = (
      <Chip
        accessory={option.accessory}
        label={option.label}
        state={
          (option.state === 'disabled' && 'disabled') ||
          (isHighlighted && 'highlighted') ||
          'default'
        }
        type="presentation"
      />
    )
  } else if (option.variant === 'column') {
    content = (
      <div css={styles.menuOptionColumn}>
        <div css={styles.menuOptionColumnTicker}>
          <MenuOptionText
            color={
              option.state === 'disabled' ? NeutralColorToken.Fg3 : undefined
            }
            isTruncated
            substring={substring}
            substringMinMatchLength={1}
          >
            {option.label}
          </MenuOptionText>
        </div>
        {option.caption && (
          <div css={styles.menuOptionColumnCaption}>
            <MenuOptionText
              color={
                option.state === 'disabled' ? NeutralColorToken.Fg3 : undefined
              }
              isTruncated
              substring={substring}
              substringMinMatchLength={1}
            >
              {option.caption}
            </MenuOptionText>
          </div>
        )}
      </div>
    )
  } else {
    content = (
      <>
        {(!option.variant ||
          option.variant === 'default' ||
          option.variant === 'button') &&
          (isSelected || !!option.icon) && (
            <div css={styles.menuOptionIcon}>
              <FoundationIcon type={option.icon ?? 'Checkmark16'} />
            </div>
          )}
        <div css={styles.menuOptionTexts}>
          {option.variant === 'title' ? (
            <div css={styles.menuOptionTitleContent}>
              <MenuOptionText isTitle substring={substring}>
                {option.label}
              </MenuOptionText>
              {option.variant === 'title' &&
                option.__DO_NOT_USE__DeprecatedShowMoreLink && (
                  <>
                    <div css={styles.menuOptionTitleSpacer} />
                    <TextButton
                      href={option.__DO_NOT_USE__DeprecatedShowMoreLink}
                      label="Show More"
                      type="anchor"
                      variant="standalone"
                    />
                  </>
                )}
            </div>
          ) : (
            <MenuOptionText
              color={
                isMenuOptionSelectable(option) && option.state === 'disabled'
                  ? NeutralColorToken.Fg3
                  : undefined
              }
              isButton={option?.variant === 'button'}
              substring={substring}
            >
              {option.label}
            </MenuOptionText>
          )}
          {(!option.variant || option.variant === 'default') &&
            option.caption && (
              <MenuOptionText
                color={
                  isMenuOptionSelectable(option) && option.state === 'disabled'
                    ? NeutralColorToken.Fg3
                    : NeutralColorToken.Fg2
                }
                substring={substring}
              >
                {option.caption}
              </MenuOptionText>
            )}
        </div>
      </>
    )
  }
  return (
    <li
      ref={innerRef}
      aria-disabled={
        (isMenuOptionSelectable(option) && option.state === 'disabled') ||
        undefined
      }
      css={
        option.variant === 'chip'
          ? stylesVariantSearch.menuOptionChip
          : [
              variant === 'search'
                ? stylesVariantSearch.menuOption
                : styles.menuOption,
              isInset &&
                !isSelected &&
                (!option.variant ||
                  option.variant === 'default' ||
                  option.variant === 'button') &&
                !option.icon &&
                styles.menuOptionInset,
              option.variant === 'title' &&
                (variant === 'search'
                  ? stylesVariantSearch.menuOptionTitle
                  : styles.menuOptionTitle),
              isHighlighted && styles.menuOptionHighlighted,
              isMenuOptionSelectable(option) &&
                option.state === 'disabled' && {
                  cursor: 'not-allowed',
                },
            ]
      }
      data-cutoff={
        (variant !== 'search' &&
          index === (maxVisibleOptions ?? MAX_NUM_FULLY_VISIBLE_OPTIONS)) ||
        undefined
      }
      data-testid={option['data-testid']}
      role={option.variant === 'title' ? 'presentation' : role}
      {...props}
    >
      {content}
    </li>
  )
}

type MenuOptionTextProps = {
  children: string
  color?: React.ComponentProps<typeof Type.Text>['color']
  isButton?: boolean
  isTitle?: boolean
  isTruncated?: boolean
  substring?: string
  substringMinMatchLength?: number
}

const MenuOptionText = React.memo(function MenuOptionText({
  children,
  color,
  isButton,
  isTitle,
  isTruncated,
  substring: inputSubstring = '',
  substringMinMatchLength = 2,
}: MenuOptionTextProps) {
  const theme = useTheme()

  const substring = inputSubstring.trim()
  const canonicalSubstring = substring.trim().toLocaleLowerCase()
  const tokenizedString = React.useMemo<Array<string>>(
    () =>
      getTokenizedStringBySubstring(
        children,
        canonicalSubstring,
        substringMinMatchLength
      ),
    [canonicalSubstring, children, substringMinMatchLength]
  )

  const textColorCssValue =
    color && !isLinearGradientColor(color)
      ? getColorValue(color, theme)
      : 'currentColor'

  return isButton || isTitle ? (
    <Type.Text
      color={color}
      isBold
      maxLines={1}
      overrideRootStyles={
        isButton ? getInlineButtonStyles(textColorCssValue) : undefined
      }
      size="S"
    >
      {children}
    </Type.Text>
  ) : (
    // NOTE(yan): bolding fragments the text, so add an aria-label
    //  here to help the screen reader understand the text
    <Type.Text
      aria-label={children}
      color={color}
      maxLines={isTruncated ? 1 : undefined}
      size="S"
    >
      {tokenizedString.map((token, i) =>
        token.toLocaleLowerCase() === canonicalSubstring ? (
          <Type.Extend key={i} isBold>
            {token}
          </Type.Extend>
        ) : (
          token
        )
      )}
    </Type.Text>
  )
})

export function getTokenizedStringBySubstring(
  body: string,
  substring: string,
  minMatchLength: number
): Array<string> {
  if (!substring || substring.length < minMatchLength) {
    return [body]
  }
  if (!body.toLocaleLowerCase().includes(substring)) {
    return [body]
  }
  return body.split('').reduce<Array<string>>(
    (tokens, character, index) => {
      const lastIndex = tokens.length - 1
      const currentToken = tokens[lastIndex] + character
      if (!currentToken.toLocaleLowerCase().includes(substring)) {
        tokens[lastIndex] = currentToken
        return tokens
      }

      // NOTE(yan): we know the matched substring is at the end of currentToken
      //  so slit currentToken to isolate the matched substring
      const stringLeftover = currentToken.slice(0, -substring.length)
      const stringMatchedSubstring = currentToken.slice(-substring.length)
      if (stringLeftover.length > 0) {
        tokens[lastIndex] = stringLeftover
        tokens.push(stringMatchedSubstring)
      } else {
        tokens[lastIndex] = stringMatchedSubstring
      }
      if (index !== body.length - 1) {
        // more characters to process, add empty string to build off of
        tokens.push('')
      }
      return tokens
    },
    ['']
  )
}

// NOTE(yan):
//  defining as Exclude<MenuOptionType<TOption>, MenuOptionVariantTitle> would
//    not work due to typescript limitation around Exclude and generics. see:
//  https://github.com/microsoft/TypeScript/issues/28884
//  https://stackoverflow.com/a/64920838
export type MenuOptionVariantExcludeTitle<
  TOption extends MenuOptionVariantSelectable
> = TOption | MenuOptionVariantButton

export type MenuOptionsGroupType<T extends MenuOptionVariantSelectable> =
  | Array<MenuOptionVariantExcludeTitle<T>>
  | [MenuOptionVariantTitle, ...Array<MenuOptionVariantExcludeTitle<T>>]

export type MenuOptionsListType<
  TOption extends MenuOptionVariantSelectable = MenuOptionVariantSelectable
> = MenuOptionsGroupType<TOption> | Array<MenuOptionsGroupType<TOption>>

type MenuOptionsListBaseProps = {
  isMenuOpen: boolean
  minWidth?: React.CSSProperties['minWidth']
  width?: React.CSSProperties['width']
}
type MenuOptionsListProps<
  T extends MenuOptionVariantSelectable = MenuOptionVariantSelectable
> = MenuOptionsListBaseProps & {
  children: (
    option: MenuOptionVariantExcludeTitle<T>,
    index: number
  ) => React.ReactNode
  innerRef:
    | React.MutableRefObject<HTMLUListElement | HTMLDivElement | null>
    | React.RefCallback<HTMLUListElement | HTMLDivElement | null>
  optionsList: MenuOptionsListType<T>
  selectedOptionIdentifier: string | null
  variant?: 'default' | 'search'
} & React.HTMLAttributes<HTMLUListElement | HTMLDivElement>

export function MenuOptionsList<TOption extends MenuOptionVariantSelectable>({
  children,
  innerRef: externalMenuRef,
  isMenuOpen,
  minWidth = 'auto',
  optionsList,
  selectedOptionIdentifier,
  variant,
  width = 'auto',
  ...props
}: MenuOptionsListProps<TOption>) {
  const internalMenuRef = React.useRef<
    HTMLUListElement | HTMLDivElement | null
  >(null)
  const setMenuOptionsListRefs = React.useCallback(
    (menuElement: HTMLUListElement | HTMLDivElement | null) => {
      internalMenuRef.current = menuElement
      if (typeof externalMenuRef === 'function') {
        externalMenuRef(menuElement)
      } else if (externalMenuRef && !externalMenuRef.current) {
        externalMenuRef.current = menuElement
      }
    },
    [internalMenuRef, externalMenuRef]
  )

  // NOTE(yan):
  //  delay showing the menu after opening for one update cycle to:
  //   (1) set the height of the menu a peeking 6th option if necessary, and
  //   (2) scroll the menu down to show the selected option
  //  all before the menu is visible to the user.
  const [isReady, setIsReady] = React.useState(false)
  const wasMenuOpen = React.useRef<boolean>(isMenuOpen)
  const maxHeight = React.useRef<React.CSSProperties['maxHeight']>(undefined)

  React.useLayoutEffect(() => {
    const menuElement = internalMenuRef.current
    const prevMenuOpen = wasMenuOpen.current
    wasMenuOpen.current = isMenuOpen
    // ensure logic below only runs once the menu just opens
    if (prevMenuOpen === isMenuOpen || !isMenuOpen || !menuElement) {
      return
    }

    // compute the height of the menu to show the tip of the cutoff option
    const cutoffOptionEl =
      internalMenuRef?.current?.querySelector<HTMLLIElement>(`li[data-cutoff]`)
    if (cutoffOptionEl && cutoffOptionEl.offsetHeight > 0) {
      maxHeight.current =
        cutoffOptionEl.offsetTop + cutoffOptionEl.offsetHeight / 4
    }

    // set the scroll offset to show the selected option
    if (selectedOptionIdentifier) {
      const selectedOptionEl = menuElement.querySelector(
        `#${selectedOptionIdentifier}`
      )
      if (selectedOptionEl) {
        // try to position the option in the center
        const optionOffsetTop = (selectedOptionEl as HTMLElement).offsetTop
        const optionHeight = selectedOptionEl.getBoundingClientRect().height
        const menuListHeight = menuElement.getBoundingClientRect().height
        menuElement.scrollTop = Math.max(
          0,
          optionOffsetTop - menuListHeight / 2 + optionHeight / 2
        )
      }
    } else {
      // reset the scroll position to top when reopening a menu
      menuElement.scrollTop = 0
    }

    setIsReady(true)
    // NOTE(yan): only run this side effect when the menu opens
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isMenuOpen])

  const groupWrapperStyles: CSSInterpolation = [
    !isReady && { opacity: 0, visibility: 'hidden' },
    {
      maxHeight: maxHeight.current || 'auto',
      overflowX: 'hidden',
      overflowY: 'auto',
      width: '100%',
    },
  ]
  return (
    <Card
      css={[
        { minWidth, width },
        variant === 'search'
          ? stylesVariantSearch.menuOptionsList
          : styles.menuOptionsList,
      ]}
    >
      {optionsList.some(o => Array.isArray(o)) ? (
        // NOTE(yan):
        //  DOM structure between grouped options are intentionally different
        //    from non-grouped (flat) options. notably, the flat options have
        //    an <ul> as the outter element, with `role=listbox` (provided by
        //    downshift). instead, grouped options have a <div> as the outer
        //    element, with `role=listbox` also from downshift, and the nested
        //    options groups are rendered with <ul> with `role=group`.
        //  The DOM structure differences are needed for VO to properly detect
        //    and interact with both flat and grouped options. It was based on
        //    this example from W3:
        //  https://www.w3.org/TR/wai-aria-practices-1.2/examples/listbox/listbox-grouped.html
        <div ref={setMenuOptionsListRefs} css={groupWrapperStyles} {...props}>
          {
            // NOTE(yan): typescript is unable to infer that if one item in
            //  optionsList is an array (i.e. `MenuOptionsGroupType`) then
            //  the all the items are of type `MenuOptionsGroupType`
            (optionsList as Array<MenuOptionsGroupType<TOption>>).reduce<
              [React.ReactNode, number]
            >(
              (
                // NOTE(yan):
                //  the options list here is the unfiltered and unflattened
                //    options list passed to downshift. (see below in
                //    `useFlattenedAndFilteredMenuOptions` why preprocessing
                //    the options list is needed).
                //  downshift uses indices in the processed list to uniquely
                //    identify the options it is given.
                //  preceedingRenderedOptionsCount helps compute for each
                //    option rendered their index in the proccessed list passed
                //    to downshift.
                [previousRenderedGroups, preceedingRenderedOptionsCount],
                optionsGroup,
                i
              ) => [
                <>
                  {previousRenderedGroups}
                  <MenuOptionsGroup<TOption>
                    children={children}
                    key={i}
                    group={optionsGroup}
                    preceedingOptionsCount={preceedingRenderedOptionsCount}
                    role="group"
                    variant={variant}
                  />
                </>,
                preceedingRenderedOptionsCount +
                  (optionsGroup[0]?.variant === 'title'
                    ? optionsGroup.length - 1
                    : optionsGroup.length),
              ],
              [null, 0]
            )[0]
          }
        </div>
      ) : (
        <MenuOptionsGroup<TOption>
          children={children}
          css={groupWrapperStyles}
          // NOTE(yan): typescript is unable to infer that if one item in
          //  optionsList is NOT an array (i.e. `MenuOptionsGroupType`) then
          //  the all the items are of type `MenuOptionType<T>` (i.e. flat)
          group={optionsList as MenuOptionsGroupType<TOption>}
          innerRef={setMenuOptionsListRefs}
          variant={variant}
          {...props}
        />
      )}
    </Card>
  )
}

type MenuOptionsListErrorProps = MenuOptionsListBaseProps & {
  errorMessageId: string
  children: string
  innerRef:
    | React.MutableRefObject<HTMLDivElement | null>
    | React.RefCallback<HTMLDivElement | null>
  variant: 'search'
} & React.HTMLAttributes<HTMLDivElement>

export function MenuOptionsListError({
  children,
  errorMessageId,
  innerRef,
  isMenuOpen,
  minWidth = 'auto',
  width = 'auto',
  ...props
}: MenuOptionsListErrorProps) {
  return (
    <Card css={[{ minWidth, width }, stylesVariantSearch.menuOptionsList]}>
      <div
        ref={innerRef}
        css={stylesVariantSearch.menuOptionsListError}
        {...props}
      >
        <span id={errorMessageId}>
          <Type.Text size="S">{children}</Type.Text>
        </span>
      </div>
    </Card>
  )
}

function MenuOptionsGroup<TOption extends MenuOptionVariantSelectable>({
  'aria-labelledby': externalAriaLabelledBy,
  children,
  group,
  innerRef,
  preceedingOptionsCount = 0,
  role,
  variant,
  ...props
}: {
  children: MenuOptionsListProps<TOption>['children']
  group: MenuOptionsGroupType<TOption>
  innerRef?: React.Ref<HTMLUListElement>
  preceedingOptionsCount?: number
  variant?: 'default' | 'search'
} & React.HTMLAttributes<HTMLUListElement>) {
  const potentialHeadingIdentifier = `${useUniqueId()}-group-heading`
  const [titleOption, otherOptions] = splitTitleFromOptions(group)
  return (
    <ul
      ref={innerRef}
      aria-labelledby={joinOptionalStringsToSpaceSeparatedString(
        externalAriaLabelledBy,
        titleOption ? potentialHeadingIdentifier : undefined
      )}
      css={
        variant === 'search'
          ? stylesVariantSearch.menuOptionsGroup
          : styles.menuOptionsGroup
      }
      role={role || 'group'}
      {...props}
    >
      {titleOption ? (
        <>
          <MenuOption
            key={-1}
            id={potentialHeadingIdentifier}
            index={-1}
            innerRef={null}
            option={titleOption}
            variant={variant}
          />
          {otherOptions.map((o, i) => children(o, preceedingOptionsCount + i))}
        </>
      ) : (
        // NOTE(yan): again typescript is unable to infer that group does not
        //  contain title since it can be only in the first index and that
        //  check has already failed.
        (group as Array<MenuOptionVariantExcludeTitle<TOption>>).map((o, i) =>
          children(o, preceedingOptionsCount + i)
        )
      )}
    </ul>
  )
}

function splitTitleFromOptions<TOption extends MenuOptionVariantSelectable>(
  group: MenuOptionsGroupType<TOption>
): [
  MenuOptionVariantTitle | null,
  Array<MenuOptionVariantExcludeTitle<TOption>>
] {
  const [firstOption, ...rest] = group
  // NOTE(yan):
  //  even though `group` has type `MenuOptionsGroupType<T>`
  //    enforces that only the first item can be type `MenuOptionVariantTitle`
  //    strangely typescripts infers that `rest` has type
  //    `(MenuOptionVariantTitle | MenuOptionVariantExcludeTitle<T>)[]`.
  //    so unfortunatley we have to cast it again.
  //  this is likely due to the aforementioned typescript issue with generics
  //    and Exclude utility function.
  const otherOptions = rest as Array<MenuOptionVariantExcludeTitle<TOption>>
  const titleOption = firstOption?.variant === 'title' ? firstOption : null
  return [titleOption, otherOptions]
}

// NOTE(yan):
//  flattens a structured options array to be compatible with
//    downshift since it doesn't support options groups.
//  also filters out options that cannot be interacted so arrow key up/down
//    wouldn't land on an unhighlightable element.
//  see MenuOptionsList and MenuOptionsGroup components for adjustments made
//    while rendering the options.
export function useFlattenedAndFilteredMenuOptions<
  TOption extends MenuOptionVariantSelectable
>(
  options: MenuOptionsListType<TOption>
): Array<MenuOptionVariantExcludeTitle<TOption>> {
  return React.useMemo(
    () =>
      flatten(options).filter(
        (o): o is MenuOptionVariantExcludeTitle<TOption> =>
          o.variant !== 'title'
      ),
    [options]
  )
}

export function isMenuOptionSelectable<
  TOption extends MenuOptionVariantSelectable
>(option: MenuOptionType<TOption> | null | undefined): option is TOption {
  return !!(
    option &&
    (!option.variant ||
      option.variant === 'default' ||
      option.variant === 'column' ||
      option.variant === 'chip')
  )
}
